// CC-216 — approver-only, read-only payroll approval page. No uploads, compute or edit controls.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, ShieldAlert, XCircle, Send } from "lucide-react";
import { fmtPayWeek } from "./Payroll";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errText = (e) => {
  const d = e?.response?.data?.detail;
  return (typeof d === "string" ? d : d?.message) || e.message;
};
const ACTION_LABEL = { approve: "Approve", approve_with_conditions: "Approve with conditions", reject: "Reject" };
const DECISION_LABEL = { approve: "Approved", approve_with_conditions: "Approved with conditions", reject: "Rejected" };

function Tile({ label, value, testId }) {
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl p-4">
      <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">{label}</div>
      <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-1" data-testid={testId}>{value}</div>
    </div>
  );
}

function EmployeesTable({ rows }) {
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto">
      <table className="w-full text-[13px]" data-testid="approval-employees-table">
        <thead className="bg-[#fafbfc] text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
          <tr>
            <th className="text-left py-2 px-4">Employee</th>
            <th className="text-right py-2 px-4">Revenue</th>
            <th className="text-right py-2 px-4">Commission</th>
            <th className="text-right py-2 px-4">Hourly</th>
            <th className="text-right py-2 px-4">Adjustments</th>
            <th className="text-right py-2 px-4">Total pay</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-t border-[#eef1f6] align-top" data-testid={`approval-employee-${r.name}`}>
              <td className="py-2 px-4 font-semibold text-[#0e1420]">
                {r.name} <span className="text-[11px] font-normal text-[color:var(--cc-ink-muted)]">{r.pay_type}</span>
                {r.adjustments.map((a, i) => (
                  <div key={i} className="text-[11px] font-normal text-[#8a5a1a]">{money(a.amount)} · {a.reason}</div>
                ))}
              </td>
              <td className="py-2 px-4 text-right font-mono-data">{money(r.weekly_revenue)}</td>
              <td className="py-2 px-4 text-right font-mono-data">{money(r.final_commission)}</td>
              <td className="py-2 px-4 text-right font-mono-data">{r.hourly_pay ? money(r.hourly_pay) : "—"}</td>
              <td className="py-2 px-4 text-right font-mono-data">{r.adjustments_total ? money(r.adjustments_total) : "—"}</td>
              <td className="py-2 px-4 text-right font-mono-data font-bold" data-testid={`approval-employee-total-${r.name}`}>{money(r.total_pay)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DecisionStatus({ v }) {
  if (v.decision) {
    const reject = v.decision.decision === "reject";
    return (
      <div className={`rounded-xl border p-4 ${reject ? "border-[#f4c9c9] bg-[#fdecec]" : "border-[#c1eddc] bg-[#e6f9f2]"}`} data-testid="approval-decision-status">
        <div className="font-semibold text-[14px] text-[#0e1420]">
          {DECISION_LABEL[v.decision.decision]} by {v.decision.decided_by} · {new Date(v.decision.decided_at).toLocaleString()}
        </div>
        {v.decision.note && <div className="text-[13px] mt-1 whitespace-pre-wrap" data-testid="approval-decision-note">{v.decision.note}</div>}
        {reject && <div className="text-[12px] mt-1 text-[color:var(--cc-ink-muted)]">The payroll editor has been notified and must re-send for approval.</div>}
      </div>
    );
  }
  if (v.approved_by) {
    return <div className="text-[13px] font-semibold text-[#0e6b52]" data-testid="approval-approved-status">
      {v.approval_override ? "Override-approved" : "Approved"} by {v.approved_by}</div>;
  }
  if (!v.send_for_approval_current) {
    return <div className="text-[13px] text-[color:var(--cc-ink-muted)]" data-testid="approval-awaiting-send">
      Not sent for approval at the current numbers. You will get a fresh email once the payroll editor sends it.</div>;
  }
  if (!v.viewer_is_approver) {
    return <div className="text-[13px] text-[color:var(--cc-ink-muted)]" data-testid="approval-view-only">
      View only — the decision is recorded by the payroll approver.</div>;
  }
  return null;
}

function DecisionPanel({ onDecide, busy }) {
  const [kind, setKind] = useState(null);
  const [note, setNote] = useState("");
  const needsNote = kind && kind !== "approve";
  const ok = kind && (!needsNote || note.trim().length >= 10);
  const btn = (k, label, Icon, cls) => (
    <button type="button" onClick={() => setKind(k)} data-testid={`approval-choose-${k}`}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-semibold border transition-colors ${kind === k ? cls : "bg-white border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8]"}`}>
      <Icon size={14} /> {label}
    </button>
  );
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 space-y-3" data-testid="approval-decision-panel">
      <div className="flex flex-wrap gap-2">
        {btn("approve", "Approve", ShieldCheck, "bg-[#0e8a68] text-white border-[#0e8a68]")}
        {btn("approve_with_conditions", "Approve with conditions", ShieldAlert, "bg-[#f4b400] text-[#0e1420] border-[#f4b400]")}
        {btn("reject", "Reject", XCircle, "bg-[#ef5757] text-white border-[#ef5757]")}
      </div>
      {needsNote && (
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3}
                  placeholder={kind === "reject" ? "What needs to change? (required, 10+ characters)" : "Conditions (required, 10+ characters)"}
                  data-testid="approval-note-input"
                  className="w-full border border-[#e7e9ee] rounded-lg p-2 text-[13px] outline-none focus:border-[#0072bc]" />
      )}
      {kind && (
        <button type="button" disabled={!ok || busy} onClick={() => onDecide(kind, note)} data-testid="approval-submit-btn"
                className="bg-[#0072bc] text-white text-[13px] font-semibold px-4 py-2 rounded-lg hover:bg-[#005f9c] disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? "Submitting…" : `Submit: ${ACTION_LABEL[kind]}`}
        </button>
      )}
    </div>
  );
}

export default function PayrollApproval() {
  const { slug, runId } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/payroll/approval/runs/${runId}?tenant_slug=${slug}`)
    .then(({ data }) => { setV(data); setErr(null); }).catch((e) => setErr(errText(e)));
  useEffect(() => { load(); }, [slug, runId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onDecide = async (decision, note) => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/payroll/approval/runs/${runId}/decision?tenant_slug=${slug}`, { decision, note });
      setV(data.view);
    } catch (e) { setErr(errText(e)); await load(); }
    finally { setBusy(false); }
  };
  if (!v) {
    return <div className="p-6 text-[13px]" data-testid="approval-page-state">{err ? <span className="text-[#ef5757]" data-testid="approval-error">{err}</span> : "Loading…"}</div>;
  }
  return (
    <div className="space-y-4 max-w-[1100px]" data-testid="payroll-approval-page">
      <div>
        <div className="font-display font-bold text-[19px] text-[#0e1420]">Payroll approval</div>
        <div className="text-[12.5px] text-[color:var(--cc-ink-muted)]" data-testid="approval-pay-week">
          Pay week (Mon–Sun): {fmtPayWeek(v.pay_week_monday)} → {fmtPayWeek(v.pay_week_sunday)}
        </div>
        {v.sent_for_approval_by && (
          <div className="text-[12px] text-[#1c6a99] mt-1 flex items-center gap-1"><Send size={12} /> Sent for approval by {v.sent_for_approval_by}</div>
        )}
      </div>
      {err && <div className="text-[13px] text-[#ef5757] border border-[#f4c9c9] bg-[#fdecec] rounded-lg p-3" data-testid="approval-error">{err}</div>}
      {!v.has_compute ? (
        <div className="text-[13px]" data-testid="approval-no-compute">No numbers on file yet for this run.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Weekly revenue" value={money(v.weekly_revenue)} testId="approval-weekly-revenue" />
            <Tile label="Final commission" value={money(v.final_commission)} testId="approval-final-commission" />
            <Tile label="Techs with paylines" value={v.techs_with_paylines} testId="approval-techs-count" />
            <Tile label="Jobs deferred (pending collection)" value={v.jobs_deferred_pending_collection} testId="approval-jobs-deferred" />
          </div>
          <EmployeesTable rows={v.employees} />
          <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] flex flex-wrap gap-4">
            <span data-testid="approval-csr-total">CSR line: {money(v.csr_line_total)}{v.csr_breakdown.length ? ` (${v.csr_breakdown.map((b) => `${b.name} ${money(b.amount)}`).join(", ")})` : ""}</span>
            {v.house_account_total > 0 && <span data-testid="approval-house-total">House account (not paid out): {money(v.house_account_total)}</span>}
          </div>
        </>
      )}
      <DecisionStatus v={v} />
      {v.can_decide && <DecisionPanel onDecide={onDecide} busy={busy} />}
    </div>
  );
}
